import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { calculateStreak, getSuperlative, isEmpty } from './functions';
import moment from 'moment';

export const updateDocument = async (
  collectionName,
  queryConstraints,
  updateData,
) => {
  const q = query(collection(db, collectionName), ...queryConstraints);
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    const docRef = querySnapshot.docs[0].ref;
    await updateDoc(docRef, updateData);
    return querySnapshot.docs[0].data();
  }
  return null;
};

export const addSessionToBook = async (
  bookID,
  userID,
  session,
  endPage,
  time,
  user,
  currentBook,
  review,
  rating,
  fetchUserData,
  bookCompleted,
  showNotification,
  checkAchievements,
  t,
  fetchBooks,
  isManual,
  setGlobalState,
  globalState,
) => {
  const userBooksUpdateData = {
    sessions: arrayUnion(session),
    pages: parseInt(endPage),
    time: 0,
  };

  const userBooksData = await updateDocument(
    'UserBooks',
    [where('bookID', '==', bookID), where('userID', '==', userID)],
    userBooksUpdateData,
  );

  if (userBooksData) {
    userBooksUpdateData.time = userBooksData.time
      ? userBooksData.time + time
      : time;

    if (!userBooksData.dateStarted) {
      userBooksUpdateData.dateStarted = new Date();
    }

    if (parseInt(currentBook.number_of_pages) === parseInt(endPage)) {
      userBooksUpdateData.dateFinished = new Date();
      userBooksUpdateData.status = 'read';
    }

    if (review) {
      userBooksUpdateData.review = review;
    }

    if (rating) {
      userBooksUpdateData.rating = rating;
    }

    await updateDocument(
      'UserBooks',
      [where('bookID', '==', bookID), where('userID', '==', userID)],
      userBooksUpdateData,
    );
  }

  // Update Users collection
  session.bookID = bookID;

  const currentlyReading =
    parseInt(currentBook.number_of_pages) === parseInt(endPage)
      ? {}
      : {
          title: currentBook.title,
          author_name: currentBook.author_name,
          number_of_pages: currentBook.number_of_pages,
          pages: parseInt(endPage),
          time: currentBook.time + time,
          publish_date: currentBook.publish_date,
          publisher: currentBook.publisher,
          isbn: currentBook.isbn,
          cover_url: currentBook.cover_url,
          bookID: currentBook.bookID,
        };

  const usersUpdateData = {
    sessions: arrayUnion(session),
    currentlyReading,
  };

  await updateDocument(
    'Users',
    [where('email', '==', user.email)],
    usersUpdateData,
  );

  // Re-fetch the updated user data
  const updatedUser = await fetchUserData(
    user.email,
    false,
    'afterSessionUpdate',
  );

  if (updatedUser) {
    // Update users with the latest user data
    const streakData = calculateStreak(
      updatedUser.sessions,
      isManual ? null : updatedUser.streak?.streakStartDate,
    );

    if (parseInt(currentBook.number_of_pages) === parseInt(endPage)) {
      bookCompleted = 1;
    }

    // Check if user unlocked any achievements and set today's progress
    const now = new Date();

    let userProgress = {
      booksCompleted: 0,
      currentStreak: 0,
      longestSession: 0,
      pagesRead: 0,
      timeRead: 0,
      sessions: 0,
      today: {
        date: now,
        time: 0,
        pages: 0,
        sessions: 0,
      },
      achievementsUnlocked: [],
      achievementsHistory: [],
    };

    const achievements = user.achievements;
    let isSameDay = false;
    if (!isEmpty(achievements)) {
      userProgress = achievements;
      isSameDay =
        moment(now).format('L') ===
        moment(userProgress.today.date.toDate()).format('L');
    }

    const today = {
      date: isSameDay ? now : new Date(),
      time: isSameDay ? userProgress.today.time + time : time,
      pages: isSameDay
        ? userProgress.today.pages +
          (endPage - (currentBook.pages ? currentBook.pages : 0))
        : endPage - (currentBook.pages ? currentBook.pages : 0),
      sessions: isSameDay ? userProgress.today.sessions + 1 : 1,
    };

    const updatedUserProgress = {
      booksCompleted: userProgress.booksCompleted + bookCompleted,
      currentStreak: streakData.currentStreak,
      longestSession:
        !isManual && time > userProgress.longestSession
          ? time
          : userProgress.longestSession,
      pagesRead: !isManual
        ? userProgress.pagesRead +
          endPage -
          (currentBook.pages ? currentBook.pages : 0)
        : userProgress.pagesRead,
      timeRead: userProgress.timeRead + (!isManual ? time : 0),
      sessions: userProgress.sessions + (!isManual ? 1 : 0),
      today: today,
      achievementsUnlocked: userProgress.achievementsUnlocked || [],
      achievementsHistory: userProgress.achievementsHistory || [],
    };

    checkAchievements(updatedUserProgress, isManual);

    const updateData = {
      achievements: updatedUserProgress,
      streak: streakData,
    };

    if (
      streakData.longestStreak > updatedUser.streak?.longestStreak ||
      !updatedUser.streak ||
      (streakData.longestStreak >= updatedUser.streak?.longestStreak &&
        streakData.streakStartDate !== updatedUser.streak?.streakStartDate)
    ) {
      showNotification(
        `${getSuperlative(streakData.longestStreak, t)}! ${t.longeststreak}: ${streakData.longestStreak}`,
      );
    }

    await updateDocument(
      'Users',
      [where('email', '==', user.email)],
      updateData,
    );

    await fetchUserData(user.email, false, 'update streak, TopBar.js');
    fetchBooks();

    setGlobalState({
      ...globalState,
      share: {
        title: currentBook.title,
        author_name: currentBook.author_name,
        currentStreak: streakData.currentStreak,
        pagesRead: endPage - (currentBook.pages ? currentBook.pages : 0),
        secondsRead: time,
        bookCompleted: bookCompleted === 1,
        date: new Date(),
        showShareCard: true,
        cover_url: currentBook.cover_url,
        bookID: currentBook.bookID,
      },
    });
  }

  // More Firebase-related functions
};
